import React, { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import buffer from '@turf/buffer'

const STYLES = {
  polo: { color: '#EF7A7A', weight: 1 },
  lotes: { color: '#81ADD2', weight: 1 },
  quadras: { color: '#81ADD2', weight: 1, opacity: 0.1 },
  quadraLotes: { color: '#555', opacity: 0.2, fillOpacity: 0, weight: 0.5 },
  surroundingQuadras: { color: '#ddd', weight: 1, opacity: 0.1 },
}

export default function PoloMap({ poloId, className = '' }) {
  const mapRef = useRef(null)
  const mapInstance = useRef(null)

  useEffect(() => {
    if (!mapRef.current || !poloId || mapInstance.current) return

    // Initialize map
    const map = L.map(mapRef.current).setView([-23.55, -46.6259], 14)
    mapInstance.current = map

    // Add base layer
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map)

    // Bind click handlers
    const bindLote = (feature, layer) => {
      layer.on('click', (e) => {
        const { id } = e.target.feature
        window.location.href = `/lotes/${id}`
      })
    }

    const bindQuadra = (feature, layer) => {
      layer.on('click', (e) => {
        window.location.href = `/quadras/${e.target.feature.properties.id}`
      })
    }

    // Fetch and display layers
    const poloUrl = `/polos/${poloId}.json`
    const surroundingQuadrasUrl = `/polos/${poloId}/surrounding_quadras`
    const quadrasUrl = `/polos/${poloId}/quadras`
    const quadrasLotesUrl = `/polos/${poloId}/quadra_lotes`
    const lotesUrl = `/polos/${poloId}/lotes`

    // Polo boundary
    fetch(poloUrl)
      .then((res) => res.json())
      .then((data) => L.geoJSON(data, { style: STYLES.polo }).addTo(map))
      .catch(console.error)

    // Surrounding quadras
    fetch(surroundingQuadrasUrl)
      .then((res) => res.json())
      .then((data) =>
        L.geoJSON(data, {
          style: STYLES.surroundingQuadras,
          onEachFeature: bindQuadra,
        }).addTo(map)
      )
      .catch(console.error)

    // Quadras with buffer
    fetch(quadrasUrl)
      .then((res) => res.json())
      .then((data) => {
        const bufferedQuadras = buffer(data, 4, { units: 'meters' })
        const quadrasLayer = L.geoJSON(bufferedQuadras, {
          style: STYLES.quadras,
          onEachFeature: bindQuadra,
        })
        quadrasLayer.addTo(map)
        map.fitBounds(quadrasLayer.getBounds())
      })
      .catch(console.error)

    // Quadra lotes
    fetch(quadrasLotesUrl)
      .then((res) => res.json())
      .then((data) =>
        L.geoJSON(data, {
          style: STYLES.quadraLotes,
          onEachFeature: bindLote,
        }).addTo(map)
      )
      .catch(console.error)

    // Lotes
    fetch(lotesUrl)
      .then((res) => res.json())
      .then((data) =>
        L.geoJSON(data, {
          style: STYLES.lotes,
          onEachFeature: bindLote,
        }).addTo(map)
      )
      .catch(console.error)

    // Cleanup
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [poloId])

  return (
    <div
      ref={mapRef}
      className={`h-[400px] md:h-[600px] w-full rounded border border-gray-800 ${className}`}
    />
  )
}
